'use client';

import * as DS from '@aurorasolar/ds';
import { Children, ReactNode, useMemo } from 'react';

type Spacing = number | string | { _?: number | string; lg?: number | string; md?: number | string };

type Props = {
  bg?: string;
  children?: ReactNode;
  colorMode?: 'light' | 'dark';
  columnGap?: number | string;
  columns?: 2 | 3 | 4;
  pb?: Spacing;
  pt?: Spacing;
  rowGap?: number | string;
};

const getGridSize = (columns?: number) => {
  if (columns === 2) return { _: 'full', md: 'half' } as const;
  if (columns === 3) return { _: 'full', md: 'third' } as const;
  if (columns === 4) return { _: 'full', md: 'quarter' } as const;
  return { _: 'full' } as const;
};

export const ColumnSection = ({
  bg,
  children,
  colorMode = 'light',
  columnGap = 4,
  columns = 2,
  pb = { _: 7, lg: 10 },
  pt = { _: 7, lg: 42 },
  rowGap,
}: Props) => {
  const ModeProvider = colorMode === 'dark' ? DS.DarkModeProvider : DS.LightModeProvider;

  const childNodes: ReactNode[] = useMemo(() => {
    return Children.toArray(children);
  }, [children]);

  return (
    <ModeProvider>
      <DS.Flex bg={bg ?? (colorMode === 'dark' ? 'uiBgAlternate' : undefined)}>
        <DS.Flex maxW={DS.BorealisTheme.screens.xl} mx="auto" pb={pb} position="relative" pt={pt} px={{ _: 6, lg: 10 }}>
          {/* DS.Grid is always 12 columns and lays out all 11 column gaps even when items span
              full width, so a gap of N forces a minimum grid width of 11*N. Items are single-column
              below md (see getGridSize), so the gutter must not apply there or it overflows mobile. */}
          <DS.Grid columnGap={{ _: 0, md: columnGap }} flex={1} rowGap={rowGap} zIndex={1}>
            {childNodes.map((child, i) => (
              <DS.GridItem
                alignItems="center"
                gap={4}
                justifyContent="center"
                key={i} // eslint-disable-line react/no-array-index-key
                size={getGridSize(columns)}
              >
                {child}
              </DS.GridItem>
            ))}
          </DS.Grid>
        </DS.Flex>
      </DS.Flex>
    </ModeProvider>
  );
};
